// Eigene React-Flow-Knoten-Renderer im Blackfort-CI.
//
// PlantNode: Symbol/Sensor der Anlagen-Ansicht (erkennbares P&ID-Icon aus plantSymbols +
// Beschriftung; Icon-Auflösung datengetrieben über Symbol-ID mit Kategorie-Fallback).
// SignalNode: Blocktyp/Sonder-Knoten der Signalgraph-Ansicht mit 1-indizierten Ports als
// benannte Handles ('in-<n>'/'out-<n>'), damit buildGraph die Portnummern zurückgewinnt.

import { Handle, Position, type NodeProps } from '@xyflow/react';
import type { JSX } from 'react';
import { inHandle, outHandle, type GraphNodeData } from '../graph/build';
import { accentForKind } from '../graph/signalKind';
import type { SignalKind } from '../api/types';
import { PlantSymbolIcon } from './plantSymbols';

// Nutzlast eines Anlagen-Knotens.
export interface PlantNodeData extends Record<string, unknown> {
  symbolId: string;
  label: string;
  category: string; // 'sensor' | 'behaelter' | 'maschine' | 'aktor_sollwert'
  isaTag?: string;
}

export function PlantNode({ data, selected }: NodeProps): JSX.Element {
  const d = data as PlantNodeData;
  return (
    <div
      className={
        'relative flex w-32 flex-col items-center gap-1 rounded-lg border bg-bf-navy-light px-3 py-2 text-center shadow ' +
        (selected ? 'border-bf-gold' : 'border-bf-border')
      }
    >
      {/* Anschluss-Stutzen der Verrohrung: Ziel links, Quelle rechts. Damit docken die
          schematic-Kanten (Rohrleitungen) an — ohne Handles zeichnet React-Flow sie nicht. Ein
          Symbol kann Zulauf (links) und Ablauf (rechts) zugleich haben. */}
      <Handle
        id="pipe-in"
        type="target"
        position={Position.Left}
        style={{ background: '#7A8FAF', width: 8, height: 8 }}
      />
      <Handle
        id="pipe-out"
        type="source"
        position={Position.Right}
        style={{ background: '#7A8FAF', width: 8, height: 8 }}
      />
      {/* Echtes P&ID-Symbol je Symbol-ID (Kategorie-Fallback). Gold-Strich im CI. */}
      <PlantSymbolIcon
        symbolId={d.symbolId}
        category={d.category}
        title={d.label}
        className="h-10 w-10 text-bf-gold"
      />
      <span className="line-clamp-2 text-xs font-medium text-bf-white">{d.label}</span>
      {d.isaTag && <span className="text-[10px] text-bf-muted">{d.isaTag}</span>}
    </div>
  );
}

// Farbe je Knotenart: Katalog-Ein/-Ausgang und Modell heben sich vom Standard-Block ab.
//
// Ist die Signalart der gebundenen Variable bekannt (Issue #352), schlägt sie den Knotentyp: ein
// catalog-input auf einen Strompreis ist zwar technisch ein Eingang, aber eben KEIN Messwert der
// Anlage, und genau das soll man im Bild sehen.
function accentFor(blockType: string, signalKind?: string): string {
  if (signalKind) {
    const byKind = accentForKind(signalKind as SignalKind);
    if (byKind !== 'border-bf-border') return byKind;
  }
  if (blockType === 'catalog-input') return 'border-emerald-500/60';
  if (blockType === 'catalog-output') return 'border-sky-500/60';
  if (blockType === 'signal-filter') return 'border-teal-500/60';
  if (blockType === 'mpc-optimizer') return 'border-amber-500/60';
  if (blockType === 'model-inference') return 'border-bf-gold';
  return 'border-bf-border';
}

export function SignalNode({ data, selected }: NodeProps): JSX.Element {
  const d = data as GraphNodeData;
  // Unbekannter Superblock (Issue #271): amber-Rahmen, unabhängig von der Auswahl-Hervorhebung.
  const unknown = d.blockType === 'superblock' && d.unknownSuperblock === true;
  const accent = unknown
    ? 'border-amber-400'
    : selected
      ? 'border-bf-gold'
      : accentFor(d.blockType, d.signalKind);
  const tag = typeof d.params?.tag === 'string' ? (d.params.tag as string) : undefined;
  const ref = typeof d.params?.ref === 'string' ? (d.params.ref as string) : undefined;

  return (
    <div
      data-testid={`signal-node-${d.blockType}`}
      data-unknown={unknown ? 'true' : undefined}
      className={`relative min-w-36 rounded-lg border bg-bf-navy-light shadow ${accent}`}
    >
      {/* Eingangs-Handles links, 1-indiziert */}
      {d.inputs.map((port, i) => (
        <Handle
          key={`in-${port}`}
          id={inHandle(port)}
          type="target"
          position={Position.Left}
          style={{ top: portTop(i, d.inputs.length), background: '#C49B18' }}
        />
      ))}
      {/* Ausgangs-Handles rechts, 1-indiziert */}
      {d.outputs.map((port, i) => (
        <Handle
          key={`out-${port}`}
          id={outHandle(port)}
          type="source"
          position={Position.Right}
          style={{ top: portTop(i, d.outputs.length), background: '#C49B18' }}
        />
      ))}

      <div className="px-3 py-2">
        <div className="flex items-center gap-1">
          <div className="text-xs font-semibold text-bf-white">{d.label}</div>
          {unknown && (
            <span
              data-testid="signal-node-superblock-unknown"
              title={ref ? `Unbekannter Superblock: ${ref}` : 'Unbekannter Superblock'}
              className="rounded bg-amber-400/20 px-1 text-[10px] font-semibold text-amber-300"
            >
              ?
            </span>
          )}
        </div>
        <div className="mt-0.5 text-[10px] uppercase tracking-wider text-bf-muted">
          {d.blockType}
        </div>
        {tag && <div className="mt-1 text-[11px] text-bf-gold">{tag}</div>}
      </div>
    </div>
  );
}

// Verteilt mehrere Ports gleichmäßig über die Knotenhöhe (Prozentangabe für das Handle-top).
function portTop(index: number, count: number): string {
  if (count <= 1) return '50%';
  const step = 100 / (count + 1);
  return `${step * (index + 1)}%`;
}

// Nutzlast eines Gruppen-Rahmens: dezente Hintergrund-Fläche, die die zusammengehörigen
// Signalknoten optisch bündelt. `label` = Anzeigename der Herkunft (z. B. „Gebläse"),
// `functionLabel` = funktionale Bezeichnung des Musters (z. B. „Belüftung – DO-Regelung").
export interface GroupNodeData extends Record<string, unknown> {
  label: string;
  functionLabel?: string;
  width: number;
  height: number;
}

// Gruppen-Rahmen im Blackfort-CI: Navy-Fläche mit dünnem Gold-Rahmen und ruhiger Beschriftung.
// Nicht auswählbar/nicht ziehbar (reiner Hintergrund) — er liegt hinter den Signalknoten.
export function GroupNode({ data }: NodeProps): JSX.Element {
  const d = data as GroupNodeData;
  return (
    <div
      data-testid="signal-group"
      className="pointer-events-none rounded-xl border border-bf-gold/30 bg-bf-navy-light/40"
      style={{ width: d.width, height: d.height }}
    >
      <div className="flex flex-col gap-0.5 px-3 py-2">
        <span className="text-xs font-semibold tracking-wide text-bf-gold/90">{d.label}</span>
        {d.functionLabel && (
          <span className="text-[10px] uppercase tracking-wider text-bf-muted">
            {d.functionLabel}
          </span>
        )}
      </div>
    </div>
  );
}
